import { NoSsr } from "@mui/base";
import { PageType } from "@product/scmp-sdk";
import { useSetAtom } from "jotai";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";

import { useAppBarSetStickyScrollHeight } from "~/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { BaseLink } from "~/components/common/base-link";
import { hamburgerMenuAtom } from "~/components/hamburger-menu/atoms";
import { DefaultHeaderSwapThreshold } from "~/components/header/consts";
import IconMenu from "~/components/header/header-icon/menu.svg";
import { useHeader, useSwapOnScrollEffect } from "~/components/header/hooks";
import { ActionButton } from "~/components/header/styles";
import type { HeaderConfiguration } from "~/components/header/types";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { section as sectionData } from "~/data";
import { useEditionValue } from "~/lib/edition";
import { useCurrentPageType, useGetHomeAsPath } from "~/lib/router/hooks";
import { page as HomePage } from "~/pages/home";
import { page as SectionPage } from "~/pages/section";

import {
  ActionButtonContainer,
  Container,
  HeaderContainer,
  HeaderLogo,
  HeaderMainLogoContainer,
  LogoTextLink,
  SectionStyleHeaderContainer,
  StyleContentHeaderDivider,
  StyledDynamicHeaderOnelineMenuContainer,
  StyledLogoContainer,
  StyledLogoFlag,
  StyledLogoText,
} from "./styles";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
};

export const StyleContentHeader: FunctionComponent<Props> = ({
  className,
  headerConfiguration,
}) => {
  const { query } = useRouter();
  const edition = useEditionValue();
  const homeAsPath = useGetHomeAsPath();
  const currentPageType = useCurrentPageType();
  const setHamburgerMenuState = useSetAtom(hamburgerMenuAtom);
  const { reference } = useAppBarSetStickyScrollHeight();
  const { shouldSwap } = useSwapOnScrollEffect(
    (headerConfiguration?.appBarStickyScrollHeight ?? 0) || DefaultHeaderSwapThreshold,
  );

  const isSectionStyleMainPage =
    currentPageType === PageType.Section && query.entityId === sectionData.style.entityId;

  const baseStyleHeaderConfiguration: HeaderConfiguration = {
    responsiveFeatures: {
      desktopUp: {
        default: ["subscription", "search", "dashboard"],
      },
      largeDesktopUp: {
        default: ["subscription", "search", "my-news", "dashboard"],
      },
      mediumDesktopUp: {
        default: ["subscription", "search", "my-news", "dashboard"],
      },
      mobileUp: {
        default: ["dashboard"],
      },
      tabletUp: {
        default: ["subscription", "search", "dashboard"],
      },
    },
  };

  const { mastheadQueryParameter, renderFeatures, responsiveLogoVariant } = useHeader(shouldSwap, {
    ...headerConfiguration,
    ...(currentPageType === PageType.Article
      ? {
          responsiveFeatures: {
            ...baseStyleHeaderConfiguration.responsiveFeatures,
            mobileUp: {
              default: ["dashboard"],
              swapped: ["subscription", "content-comment", "content-share", "content-bookmark"],
            },
          },
          responsiveLogoVariant: {
            desktopUp: {
              default: "full",
            },
            largeDesktopUp: {
              default: "full",
            },
            mobileUp: {
              default: "full",
              swapped: "logo",
            },
          },
        }
      : {
          responsiveFeatures: {
            ...baseStyleHeaderConfiguration.responsiveFeatures,
            mobileUp: {
              default: ["dashboard"],
            },
          },
          responsiveLogoVariant: {
            desktopUp: {
              default: "logo",
              swapped: "full",
            },
            largeDesktopUp: {
              default: "logo",
              swapped: "full",
            },
            mobileUp: {
              default: "logo",
              swapped: "full",
            },
            tabletUp: { default: "logo", swapped: "full" },
            xLargeDesktopUp: {
              default: "logo",
              swapped: "full",
            },
          },
        }),
  });

  const { pathname: styleSectionPagePathname, query: styleSectionPageQuery } = SectionPage.route({
    appBarAdStatus: "Enabled",
    appBarVariant: headerConfiguration?.appBarVariant,
    entityId: sectionData.style.entityId,
    slide: "main",
  });
  const { pathname: homePagePathname, query: homePageQuery } = HomePage.route({
    edition,
    slide: "main",
  });

  return (
    <SectionStyleHeaderContainer $isSetMaxWidth={isSectionStyleMainPage}>
      <Container $isArticlePage={currentPageType === PageType.Article} className={className}>
        {isSectionStyleMainPage && (
          <HeaderMainLogoContainer ref={reference}>
            <BaseLink
              as={sectionData.style.urlAlias}
              pathname={styleSectionPagePathname}
              query={{ ...styleSectionPageQuery, ...mastheadQueryParameter }}
            >
              <HeaderLogo />
            </BaseLink>
          </HeaderMainLogoContainer>
        )}
        <HeaderContainer>
          <StyledLogoContainer $responsiveVariants={responsiveLogoVariant}>
            <BaseLink
              as={homeAsPath}
              pathname={homePagePathname}
              query={{ ...homePageQuery, ...mastheadQueryParameter }}
            >
              <StyledLogoFlag />
            </BaseLink>
            <LogoTextLink
              as={sectionData.style.urlAlias}
              pathname={styleSectionPagePathname}
              query={{ ...styleSectionPageQuery, ...mastheadQueryParameter }}
            >
              <StyledLogoText />
            </LogoTextLink>
          </StyledLogoContainer>
          <NoSsr>
            <StyledDynamicHeaderOnelineMenuContainer
              appBarVariant={headerConfiguration?.appBarVariant}
            />
          </NoSsr>
          <ActionButtonContainer>
            {renderFeatures()}
            <ActionButton
              $responsiveVariants={{
                desktopUp: "hidden",
                largeDesktopUp: "hidden",
                mobileUp: "show",
                tabletUp: "show",
              }}
              onClick={() => {
                setHamburgerMenuState(current => ({
                  ...current,
                  isOpen: !current.isOpen,
                  variant: "magazines-style",
                }));
                sendGATracking({
                  action: "Menu/Open",
                  category: "Menu",
                  label: "Menu/Open",
                });
              }}
            >
              <IconMenu />
            </ActionButton>
          </ActionButtonContainer>
        </HeaderContainer>
      </Container>
      <StyleContentHeaderDivider />
    </SectionStyleHeaderContainer>
  );
};

StyleContentHeader.displayName = "StyleContentHeader";
